import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { onSnapshot, collection, query, where, getDocs, doc, setDoc, updateDoc, arrayUnion, arrayRemove, serverTimestamp, deleteDoc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../../config/firebaseConfig';
import webrtcService from './webrtcService';
import VideoCall from './VideoCall';
import JitsiMeeting from './JitsiMeeting';
import IncomingCallModal, { ensureNotificationPermission } from './IncomingCallModal';
import { markCallMissed, MISSED_CALL_AFTER_MS } from '../../utils/missedCalls';
import { displayNameOf, normalizeStoredName, resolveUserIdentity } from '../../utils/userIdentity';
import { toast } from 'react-hot-toast';

const VideoCallContext = createContext();

/** Cevaplanmayan bir arama davetinin geçerlilik süresi. */
const INVITE_TTL_MS = 60 * 1000;

export const useVideoCall = () => {
  const context = useContext(VideoCallContext);
  if (!context) {
    throw new Error('useVideoCall must be used within VideoCallProvider');
  }
  return context;
};

export const VideoCallProvider = ({ children }) => {
  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [callStatus, setCallStatus] = useState('idle');
  const [conferenceParticipants, setConferenceParticipants] = useState([]);
  const [isHost, setIsHost] = useState(false);
  const [isJitsiConference, setIsJitsiConference] = useState(false);
  const [jitsiRoomName, setJitsiRoomName] = useState(null);
  const [currentUserName, setCurrentUserName] = useState('');
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  // Uygulamanın herhangi bir yerindeyken gelen 1-1 arama daveti
  const [incomingRoomInvite, setIncomingRoomInvite] = useState(null);
  // Oturum açmış kullanıcının e-postası. Dinleyiciler mount anında
  // auth.currentUser'ı okuyamaz: sağlayıcı, oturum Firebase tarafından geri
  // yüklenmeden ÖNCE (login'den de önce) mount olur ve currentUser o anda
  // null'dur. onAuthStateChanged ile takip edip dinleyicileri buna bağlıyoruz;
  // yoksa "gelen arama" dinleyicisi hiçbir zaman kurulmaz ve aranan taraf
  // hangi sayfada olursa olsun çalmaz.
  const [authEmail, setAuthEmail] = useState(auth.currentUser?.email || null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthEmail(user?.email || null);
    });
    return () => unsubscribe();
  }, []);

  // Başlatılan aramaların cevapsız kontrol zamanlayıcıları (roomId → timer).
  const missedTimersRef = useRef(new Map());

  /**
   * Arayan taraf için cevapsız kontrolü kurar. Süre sonunda oda hâlâ
   * 'waiting' ise (yani aranan kişi katılmadıysa) arama cevapsız işaretlenir
   * ve aranan kişiye bildirim + e-posta gider.
   */
  const scheduleMissedCheck = useCallback((roomId) => {
    if (!roomId || missedTimersRef.current.has(roomId)) return;
    const timer = setTimeout(() => {
      missedTimersRef.current.delete(roomId);
      markCallMissed({ roomId });
    }, MISSED_CALL_AFTER_MS);
    missedTimersRef.current.set(roomId, timer);
  }, []);

  const cancelMissedCheck = useCallback((roomId) => {
    const timer = missedTimersRef.current.get(roomId);
    if (timer) {
      clearTimeout(timer);
      missedTimersRef.current.delete(roomId);
    }
  }, []);

  // Sağlayıcı sökülürse bekleyen zamanlayıcılar da gitsin
  useEffect(() => {
    const timers = missedTimersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  // Aramayı sonlandırma tek seferlik olmalı. Aksi hâlde hem kullanıcının
  // tıklaması hem de Firestore dinleyicisinin 'ended' bildirimi aynı işi
  // tekrar tekrar tetikleyip onlarca "Call ended" bildirimi üretiyordu.
  const endingCallRef = useRef(false);
  // Etkin aramanın güncel değeri; callback'ler eski closure'a takılmasın diye
  const activeCallRef = useRef(null);
  // joinCallRoom aşağıda tanımlanıyor; davet kabulünde ref üzerinden çağrılır
  const joinCallRoomRef = useRef(null);

  // Leave call (for user leaving without ending for everyone)
  const handleLeaveCall = useCallback(async (roomId = null) => {
    // Aynı ayrılma işlemi birden fazla kez tetiklenmesin
    if (endingCallRef.current) return;
    endingCallRef.current = true;
    try {
      const user = auth.currentUser;
      const conferenceRoomId = roomId || activeCallRef.current?.roomId;
      
      // If this is a conference, remove user from participants list
      if (conferenceRoomId?.startsWith('conference_') && user) {
        try {
          // Get user data for the leave notification
          const usersRef = collection(db, 'Users');
          const userQuery = query(usersRef, where('eMail', '==', user.email));
          const userSnapshot = await getDocs(userQuery);
          const userData = userSnapshot.docs[0]?.data();
          const userName = userData ? `${userData.eName} ${userData.eSurname}` : user.email;

          // Remove user from conference participants
          const conferenceRef = doc(db, 'conferences', conferenceRoomId);
          const conferenceSnap = await getDoc(conferenceRef);
          
          if (conferenceSnap.exists()) {
            const currentParticipants = conferenceSnap.data().participants || [];
            // Filter out the leaving user (handle both email string and object format)
            const updatedParticipants = currentParticipants.filter(p => {
              if (typeof p === 'string') return p !== user.email;
              return p.email !== user.email;
            });
            
            await updateDoc(conferenceRef, {
              participants: updatedParticipants,
              lastLeft: { email: user.email, name: userName, leftAt: serverTimestamp() }
            });
          }

          // Also remove from callRooms
          const callRoomRef = doc(db, 'callRooms', conferenceRoomId);
          await updateDoc(callRoomRef, {
            participants: arrayRemove(user.email)
          }).catch(() => {});
        } catch (err) {
          console.error('Error removing from conference:', err);
        }
      }

      // Close peer connection without updating call status
      if (webrtcService.peerConnection) {
        webrtcService.peerConnection.close();
        webrtcService.peerConnection = null;
      }

      // Stop local stream
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
      if (webrtcService.localStream) {
        webrtcService.localStream.getTracks().forEach(track => track.stop());
        webrtcService.localStream = null;
      }

      // Unsubscribe from listeners
      if (webrtcService.unsubscribe) {
        webrtcService.unsubscribe();
        webrtcService.unsubscribe = null;
      }
      (webrtcService.candidateUnsubs || []).forEach((unsub) => {
        try { unsub(); } catch (e) { /* zaten kapalı */ }
      });
      webrtcService.candidateUnsubs = [];

      // Clear callback
      webrtcService.onRemoteTrack = null;
      webrtcService.onConnectionFailed = null;
      webrtcService.callId = null;
      webrtcService.role = null;
      webrtcService.remoteStream = null;

      setActiveCall(null);
      setLocalStream(null);
      setRemoteStream(null);
      setIsMuted(false);
      setIsVideoOff(false);
      setCallStatus('idle');
      setConferenceParticipants([]);
      setIsJitsiConference(false);
      setJitsiRoomName(null);

      toast('Aramadan ayrıldınız', { icon: 'ℹ️' });
    } catch (error) {
      console.error('Error leaving call:', error);
    } finally {
      endingCallRef.current = false;
    }
  }, [localStream]);

  // End active call (ends for everyone - used for 1-on-1 calls)
  const handleEndCall = useCallback(async () => {
    const current = activeCallRef.current;
    // Etkin arama yoksa ya da sonlandırma zaten sürüyorsa çık.
    // (Firestore dinleyicisi 'ended' durumunu birden çok kez bildirebiliyor.)
    if (!current || endingCallRef.current) return;

    try {
      // Check if this is a conference call - if so, just leave instead of ending
      if (current.roomId?.startsWith('conference_')) {
        await handleLeaveCall();
        return;
      }

      endingCallRef.current = true;
      // Durumu hemen temizle: geç gelen bildirimler yukarıdaki korumaya takılır
      activeCallRef.current = null;

      await webrtcService.endCall();

      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }

      // Mark call invitation messages as ended
      if (current.roomId) {
        try {
          const messagesRef = collection(db, 'messages');
          const q = query(messagesRef, where('callRoomId', '==', current.roomId));
          const snapshot = await getDocs(q);

          await Promise.all(
            snapshot.docs.map((docSnapshot) =>
              updateDoc(docSnapshot.ref, {
                callEnded: true,
                endedAt: serverTimestamp()
              })
            )
          );

          // Aranan taraf hiç katılmadan arama kapatıldıysa bu bir cevapsız
          // aramadır: oda 'ended' yerine 'missed' olur ve aranan kişi bildirim
          // + e-posta alır. markCallMissed yalnızca 'waiting' odaları çevirir,
          // dolayısıyla karşılıklı görüşmeler normal şekilde 'ended' kapanır.
          cancelMissedCheck(current.roomId);
          const markedMissed = await markCallMissed({ roomId: current.roomId });

          if (!markedMissed) {
            // Oda kaydını kapat ki karşı tarafta "gelen arama" ekranı kalmasın
            await updateDoc(doc(db, 'callRooms', current.roomId), {
              status: 'ended',
              endedAt: serverTimestamp()
            }).catch(() => {});
          }
        } catch (error) {
          console.error('Error updating call invitation messages:', error);
        }
      }

      setActiveCall(null);
      setLocalStream(null);
      setRemoteStream(null);
      setIsMuted(false);
      setIsVideoOff(false);
      setCallStatus('idle');

      toast('Arama sonlandı', { icon: 'ℹ️' });
    } catch (error) {
      console.error('Error ending call:', error);
    } finally {
      endingCallRef.current = false;
    }
  }, [localStream, handleLeaveCall, cancelMissedCheck]);

  // activeCall'ı ref ile senkron tut
  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  // Dinleyicilerin her render'da yeniden kurulmaması için kararlı referans.
  // Eskiden "monitor" efekti handleEndCall'a bağlıydı; handleEndCall her state
  // değişiminde yeni kimlik aldığı için efekt sürekli yeniden abone oluyor,
  // her abonelikte onSnapshot mevcut 'ended' belgesini tekrar teslim ediyor ve
  // bu döngü onlarca "Call ended" bildirimi üretiyordu.
  const handleEndCallRef = useRef(handleEndCall);
  useEffect(() => {
    handleEndCallRef.current = handleEndCall;
  }, [handleEndCall]);

  const handleLeaveCallRef = useRef(handleLeaveCall);
  useEffect(() => {
    handleLeaveCallRef.current = handleLeaveCall;
  }, [handleLeaveCall]);

  // Listen for incoming calls
  useEffect(() => {
    if (!authEmail) return;

    const callsRef = collection(db, 'calls');
    const q = query(
      callsRef,
      where('receiverId', '==', authEmail),
      where('status', '==', 'ringing')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const callData = { id: change.doc.id, ...change.doc.data() };
          setIncomingCall(callData);
          setCallStatus('ringing');
        }
      });
    });

    return () => unsubscribe();
  }, [authEmail]);

  /**
   * Uygulama geneli "gelen arama" dinleyicisi.
   *
   * Eskiden yalnızca `calls` koleksiyonu dinleniyordu; ancak 1-1 aramalarda
   * oradaki `receiverId` alanı oda kimliğiydi, kullanıcının e-postası değil.
   * Bu yüzden dinleyici hiç tetiklenmiyor, aranan kişi çağrıyı ancak
   * Mesajlar > sohbeti aç > daveti bul yolundan görebiliyordu.
   * Artık `callRooms` üzerindeki davet doğrudan dinleniyor.
   */
  useEffect(() => {
    if (!authEmail) return;

    const q = query(
      collection(db, 'callRooms'),
      where('inviteeEmail', '==', authEmail),
      where('status', '==', 'waiting')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      // Zaten bir aramadaysa yeni davet gösterilmez
      if (activeCallRef.current) return;

      const now = Date.now();
      const rooms = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      const ageOf = (room) => {
        const createdAt = room.createdAt?.toMillis?.() ?? room.createdAt?.seconds * 1000;
        return createdAt ? now - createdAt : 0;
      };

      // Uygulama kapalıyken gelmiş davetler çalmaz; bunlar doğrudan cevapsız
      // aramaya dönüşür (üst çubukta bildirim + e-posta).
      rooms
        .filter((room) => ageOf(room) > INVITE_TTL_MS)
        .forEach((room) => markCallMissed(room));

      const fresh = rooms
        .filter((room) => ageOf(room) <= INVITE_TTL_MS)
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

      setIncomingRoomInvite(fresh[0] || null);
    });

    return () => unsubscribe();
  }, [authEmail]);

  // Cevaplanmayan davet bir süre sonra kendiliğinden kapanır ve cevapsız
  // aramaya dönüşür. (Arayan taraf da aynı kontrolü yapar; işaretleme tek
  // seferlik olduğu için hangisi önce davranırsa o geçerli olur.)
  useEffect(() => {
    if (!incomingRoomInvite) return;
    const timer = setTimeout(() => {
      setIncomingRoomInvite(null);
      markCallMissed(incomingRoomInvite);
    }, INVITE_TTL_MS);
    return () => clearTimeout(timer);
  }, [incomingRoomInvite]);

  // Gelen arama davetini kabul et
  const handleAcceptRoomInvite = useCallback(async () => {
    const invite = incomingRoomInvite;
    if (!invite) return;
    setIncomingRoomInvite(null);
    cancelMissedCheck(invite.roomId || invite.id);
    try {
      await updateDoc(doc(db, 'callRooms', invite.roomId || invite.id), {
        status: 'active'
      });
    } catch (error) {
      console.debug('Oda durumu güncellenemedi:', error);
    }
    await joinCallRoomRef.current?.(invite.roomId || invite.id, invite.callType || 'video');
  }, [incomingRoomInvite, cancelMissedCheck]);

  // Gelen arama davetini reddet
  const handleRejectRoomInvite = useCallback(async () => {
    const invite = incomingRoomInvite;
    setIncomingRoomInvite(null);
    if (!invite) return;
    // Bilinçli ret cevapsız arama sayılmaz
    cancelMissedCheck(invite.roomId || invite.id);
    try {
      await updateDoc(doc(db, 'callRooms', invite.roomId || invite.id), {
        status: 'rejected',
        rejectedAt: serverTimestamp()
      });
    } catch (error) {
      console.debug('Arama reddedilemedi:', error);
    }
    toast('Arama reddedildi', { icon: 'ℹ️' });
  }, [incomingRoomInvite, cancelMissedCheck]);

  // Monitor active call status
  useEffect(() => {
    if (!activeCall?.id) return;

    // Skip monitoring for conference calls - they don't auto-end when others leave
    if (activeCall?.roomId?.startsWith('conference_')) {
      return;
    }

    // Listen to the specific call document instead of the entire collection
    const callRef = doc(db, 'calls', activeCall.id);
    const unsubscribe = onSnapshot(callRef, (snapshot) => {
      if (!snapshot.exists()) {
        // Call document was deleted
        handleEndCallRef.current?.();
        return;
      }

      const data = snapshot.data();

      if (data.status === 'ended' || data.status === 'rejected') {
        handleEndCallRef.current?.();
      } else if (data.status === 'connected') {
        setCallStatus('connected');
      }
    });

    return () => unsubscribe();
  }, [activeCall?.id, activeCall?.roomId]);

  /**
   * Arayan taraf için: karşı taraf aramayı reddederse haber ver ve aramayı
   * kapat. Reddetme `callRooms` üzerinde işaretlendiği için `calls` belgesini
   * dinleyen monitör bunu göremiyordu.
   */
  useEffect(() => {
    const roomId = activeCall?.roomId;
    if (!roomId || roomId.startsWith('conference_')) return;
    if (activeCall?.isInitiator !== true) return;

    const unsubscribe = onSnapshot(doc(db, 'callRooms', roomId), (snapshot) => {
      if (!snapshot.exists()) return;
      if (snapshot.data().status === 'rejected') {
        toast('Arama reddedildi', { icon: '📵' });
        handleEndCallRef.current?.();
      }
    });

    return () => unsubscribe();
  }, [activeCall?.roomId, activeCall?.isInitiator]);

  // Track previous participants for detecting leaves
  const prevParticipantsRef = React.useRef([]);

  // Monitor conference status - end call if conference is ended by host
  useEffect(() => {
    if (!activeCall?.roomId?.startsWith('conference_')) return;

    const conferenceRef = doc(db, 'conferences', activeCall.roomId);
    const unsubscribe = onSnapshot(conferenceRef, (snapshot) => {
      if (!snapshot.exists()) {
        // Conference was ended by host - force end for all participants
        toast('Toplantı sunucu tarafından sonlandırıldı', { icon: 'ℹ️' });
        handleLeaveCallRef.current?.();
        return;
      }

      const data = snapshot.data();
      const newParticipants = data.participants || [];
      
      // Check if someone left (show toast)
      if (data.lastLeft && data.lastLeft.email !== auth.currentUser?.email) {
        // Check if this is a new leave event (not already shown)
        const lastLeftEmail = data.lastLeft.email;
        const wasInPrev = prevParticipantsRef.current.some(p => 
          (typeof p === 'string' ? p : p.email) === lastLeftEmail
        );
        const notInNew = !newParticipants.some(p => 
          (typeof p === 'string' ? p : p.email) === lastLeftEmail
        );
        
        if (wasInPrev && notInNew) {
          const leaverName = data.lastLeft.name || data.lastLeft.email.split('@')[0];
          toast(`${leaverName} ayrıldı`, { icon: '👋' });
        }
      }
      
      // Update previous participants ref
      prevParticipantsRef.current = newParticipants;
      
      // Update participants list
      setConferenceParticipants(newParticipants);
      // Check if current user is the host
      setIsHost(data.hostEmail === auth.currentUser?.email);
    });

    return () => unsubscribe();
  }, [activeCall?.roomId]);

  // End conference for everyone (host only)
  const handleEndConference = useCallback(async () => {
    if (!activeCall?.roomId?.startsWith('conference_')) return;
    
    const user = auth.currentUser;
    if (!user) return;

    try {
      // Delete the conference document - this will trigger all participants to leave
      const conferenceRef = doc(db, 'conferences', activeCall.roomId);
      await deleteDoc(conferenceRef);

      // Also delete the callRooms document
      const callRoomRef = doc(db, 'callRooms', activeCall.roomId);
      await deleteDoc(callRoomRef);

      // Clean up local state
      await handleLeaveCall();
      
      toast.success('Conference ended');
    } catch (error) {
      console.error('Error ending conference:', error);
      toast.error('Failed to end conference');
    }
  }, [activeCall?.roomId, handleLeaveCall]);

  // Get user data helper
// Accept incoming call
  const handleAcceptCall = async () => {
    try {
      setCallStatus('connecting');

      const stream = await webrtcService.getUserMedia(true, true);
      setLocalStream(stream);

      // Set up callback for remote tracks
      webrtcService.onRemoteTrack = (remoteStream) => {
        console.log('Remote stream updated with tracks:', remoteStream.getTracks().length);
        setRemoteStream(remoteStream);
      };

      // Taşıma katmanı çökerse kullanıcı sessiz siyah ekranda bırakılmaz
      webrtcService.onConnectionFailed = () => {
        toast.error(
          'Görüşme bağlantısı kurulamadı. İki taraf farklı ağlardaysa bağlantı ' +
          'güvenlik duvarına takılmış olabilir.'
        );
      };

      await webrtcService.answerCall(incomingCall.id);

      setActiveCall(incomingCall);
      setIncomingCall(null);

      toast.success('Call connected!');
    } catch (error) {
      console.error('Error accepting call:', error);
      toast.error('Failed to accept call. Please check your camera and microphone permissions.');
      setCallStatus('idle');
    }
  };

  // Reject incoming call
  const handleRejectCall = async () => {
    try {
      if (incomingCall?.id) {
        await webrtcService.rejectCall(incomingCall.id);
      }
      setIncomingCall(null);
      setCallStatus('idle');
      toast('Call rejected', { icon: 'ℹ️' });
    } catch (error) {
      console.error('Error rejecting call:', error);
    }
  };

  // Toggle mute
  const handleToggleMute = () => {
    const muted = webrtcService.toggleMute();
    setIsMuted(muted);
  };

  // Toggle video
  const handleToggleVideo = () => {
    const videoOff = webrtcService.toggleVideo();
    setIsVideoOff(videoOff);
  };

  // Join a call room (defined before initiateVideoCall since it depends on this)
  const joinCallRoom = useCallback(async (callRoomId, callType = 'video') => {
    try {
      setCallStatus('connecting');
      // Reset video/audio state for new call
      setIsVideoOff(false);
      setIsMuted(false);

      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Görünen ad ortak çözücüden gelir (önce Employees, sonra Users);
      // kayıt bulunamazsa e-postadan okunabilir bir ad türetilir, ham e-posta
      // artık karşı tarafa gösterilmez.
      const userName = displayNameOf(await resolveUserIdentity(user.email));
      
      // Store user info for Jitsi
      setCurrentUserName(userName);
      setCurrentUserEmail(user.email);

      // Check if this is a conference call - use Jitsi for conferences
      if (callRoomId.startsWith('conference_')) {
        // Update call room to add this user as participant
        const callRoomRef = doc(db, 'callRooms', callRoomId);
        await setDoc(callRoomRef, {
          participants: arrayUnion(user.email),
          status: 'active'
        }, { merge: true });

        // Add participant with name to the conference document
        const conferenceRef = doc(db, 'conferences', callRoomId);
        const conferenceSnap = await getDoc(conferenceRef);
        
        if (conferenceSnap.exists()) {
          const conferenceData = conferenceSnap.data();
          const currentParticipants = conferenceData.participants || [];
          
          // Check if user is already in participants
          const alreadyJoined = currentParticipants.some(p => 
            (typeof p === 'string' ? p : p.email) === user.email
          );
          
          if (!alreadyJoined) {
            // Add participant object with name
            await updateDoc(conferenceRef, {
              participants: arrayUnion({ email: user.email, name: userName })
            });
          }

          // Check if current user is the host
          setIsHost(conferenceData.hostEmail === user.email);
        }

        // Use Jitsi for conference calls
        setIsJitsiConference(true);
        setJitsiRoomName(callRoomId);
        setActiveCall({
          roomId: callRoomId,
          userName: userName,
          isInitiator: false,
          callType: 'video'
        });

        toast.success('Toplantıya katılındı!');
        setCallStatus('connected');
        return;
      }

      // For non-conference calls, use existing WebRTC logic
      // Get user media
      const videoEnabled = callType === 'video';
      const stream = await webrtcService.getUserMedia(videoEnabled, true);
      setLocalStream(stream);

      if (!videoEnabled) {
        setIsVideoOff(true);
      }

      // Set up callback for remote tracks
      webrtcService.onRemoteTrack = (remoteStream) => {
        console.log('Remote stream updated with tracks:', remoteStream.getTracks().length);
        setRemoteStream(remoteStream);
      };

      // Taşıma katmanı çökerse kullanıcı sessiz siyah ekranda bırakılmaz
      webrtcService.onConnectionFailed = () => {
        toast.error(
          'Görüşme bağlantısı kurulamadı. İki taraf farklı ağlardaysa bağlantı ' +
          'güvenlik duvarına takılmış olabilir.'
        );
      };

      // Katılımcıyı odaya ekle. DİKKAT: burada status'e dokunulmaz. Eskiden
      // status koşulsuz 'active' yapılıyordu; arayan kendi odasına otomatik
      // katıldığı için davet daha karşı tarafa çalmadan 'waiting' durumundan
      // çıkıyor ve aranan kişinin "gelen arama" dinleyicisi (status=='waiting')
      // daveti anında kaybediyordu. Oda, yalnızca aranan taraf katılınca
      // aşağıda 'active' yapılır.
      const callRoomRef = doc(db, 'callRooms', callRoomId);
      await setDoc(callRoomRef, {
        participants: arrayUnion(user.email)
      }, { merge: true });

      // Get call room data
      const callRoomSnap = await getDocs(query(collection(db, 'callRooms'), where('roomId', '==', callRoomId)));
      const callRoomData = callRoomSnap.docs[0]?.data();

      // Create or answer WebRTC connection
      const isInitiator = callRoomData?.createdBy === user.email;

      // Aranan taraf odaya girdi: zil durumundan çık. (Modal üzerinden kabul
      // de bunu ayarlar; sohbetteki "Katıl" düğmesi gibi diğer yollar için
      // burada da garanti ediyoruz.)
      if (!isInitiator) {
        await updateDoc(callRoomRef, { status: 'active' }).catch(() => {});
      }

      if (isInitiator) {
        // Creator: create offer
        const callId = await webrtcService.createCall(
          user.email,
          userName,
          callRoomId, // Use room ID as receiver
          'Call Room'
        );

        setActiveCall({
          id: callId,
          roomId: callRoomId,
          userName: userName,
          isInitiator: true,
          callType
        });
      } else {
        // Joiner: wait for offer and create answer
        setActiveCall({
          roomId: callRoomId,
          userName: userName,
          isInitiator: false,
          callType
        });

        // Arayanın WebRTC teklifi (offer), kamera izni/açılışı bittikten
        // SONRA yazılır; zil ise oda belgesi yazılır yazılmaz çalar. Aranan
        // hızlı kabul ederse teklif henüz ortada olmaz. Eskiden burada tek
        // seferlik getDocs vardı: teklif bulunamayınca kod sessizce vazgeçiyor
        // ama ekran yine "bağlandı" görünüyordu — iki taraf da görüşme
        // ekranında, ortada bağlantı yok. Artık teklif görünene kadar
        // dinliyoruz (en fazla 30 sn).
        const callsQuery = query(
          collection(db, 'calls'),
          where('receiverId', '==', callRoomId)
        );

        const offerDoc = await new Promise((resolve) => {
          let settled = false;
          const finish = (result, stop) => {
            if (settled) return;
            settled = true;
            stop?.();
            resolve(result);
          };
          const stopWaiting = onSnapshot(callsQuery, (snapshot) => {
            const validCalls = snapshot.docs.filter(d => {
              const data = d.data();
              return data.offer && (data.status === 'ringing' || data.status === 'connected');
            });
            if (validCalls.length > 0) finish(validCalls[0], stopWaiting);
          }, () => finish(null, stopWaiting));
          setTimeout(() => finish(null, stopWaiting), 30000);
        });

        // Beklerken kullanıcı aramayı kapattıysa sessizce çık. (endCall ve
        // leaveCall localStream'i durdurup null'lar; activeCallRef'e
        // bakılamaz çünkü az önceki setActiveCall henüz commit edilmemiş
        // olabilir.)
        if (!webrtcService.localStream) {
          return;
        }

        if (!offerDoc) {
          throw new Error('offer-timeout');
        }

        await webrtcService.answerCall(offerDoc.id);

        setActiveCall(prev => ({
          ...prev,
          id: offerDoc.id
        }));
      }

      toast.success('Joined call!');
      setCallStatus('connected');
    } catch (error) {
      console.error('Error joining call room:', error);
      toast.error(
        error?.message === 'offer-timeout'
          ? 'Karşı taraftan bağlantı kurulamadı; arama iptal edildi.'
          : 'Aramaya katılınamadı. Kamera ve mikrofon izinlerini kontrol edin.'
      );
      setCallStatus('idle');

      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
        setLocalStream(null);
      }
    }
  }, [callStatus, localStream]);

  useEffect(() => {
    joinCallRoomRef.current = joinCallRoom;
  }, [joinCallRoom]);

  // Initiate a video call - sends a link to chat and auto-joins
  const initiateVideoCall = useCallback(async (receiverId, receiverName, receiverImage, sendMessageCallback) => {
    try {
      // Kullanıcı jesti varken tarayıcı bildirimi iznini iste; böylece bu
      // kullanıcı ilerde arandığında sekme arka plandayken de bildirim alır.
      ensureNotificationPermission();
      if (callStatus !== 'idle') {
        toast.error('You are already in a call');
        return;
      }

      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const callerName = displayNameOf(await resolveUserIdentity(user.email));

      // Create a unique call room ID
      const callRoomId = `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Create call room document in Firestore
      const callRoomRef = doc(db, 'callRooms', callRoomId);
      await setDoc(callRoomRef, {
        roomId: callRoomId,
        createdBy: user.email,
        createdByName: callerName,
        // Alıcı bilgisi olmadan karşı tarafa "gelen arama" bildirimi
        // gösterilemiyordu; bu alanlar global dinleyici tarafından okunur.
        inviteeEmail: receiverId,
        inviteeName: receiverName,
        callType: 'video',
        participants: [user.email],
        status: 'waiting',
        createdAt: serverTimestamp(),
        type: 'video'
      });

      // Send call invitation message through chat
      if (sendMessageCallback) {
        await sendMessageCallback({
          type: 'call_invitation',
          callType: 'video',
          callRoomId: callRoomId,
          callerName: callerName,
          text: `${callerName} is inviting you to a video call`
        });
      }

      toast.success('Arama başlatıldı, karşı taraf aranıyor...');

      // Karşı taraf süresi içinde katılmazsa arama cevapsız işaretlenir
      scheduleMissedCheck(callRoomId);

      // Automatically join the call room
      await joinCallRoom(callRoomId, 'video');
    } catch (error) {
      console.error('Error initiating call:', error);
      toast.error('Failed to send call invitation.');
    }
  }, [callStatus, joinCallRoom, scheduleMissedCheck]);

  // Initiate a voice call (audio only) - sends a link to chat
  const initiateVoiceCall = useCallback(async (receiverId, receiverName, receiverImage, sendMessageCallback) => {
    try {
      // Kullanıcı jesti varken tarayıcı bildirimi iznini iste; böylece bu
      // kullanıcı ilerde arandığında sekme arka plandayken de bildirim alır.
      ensureNotificationPermission();
      if (callStatus !== 'idle') {
        toast.error('You are already in a call');
        return;
      }

      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const callerName = displayNameOf(await resolveUserIdentity(user.email));

      // Create a unique call room ID
      const callRoomId = `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Create call room document in Firestore
      const callRoomRef = doc(db, 'callRooms', callRoomId);
      await setDoc(callRoomRef, {
        roomId: callRoomId,
        createdBy: user.email,
        createdByName: callerName,
        inviteeEmail: receiverId,
        inviteeName: receiverName,
        callType: 'voice',
        participants: [user.email],
        status: 'waiting',
        createdAt: serverTimestamp(),
        type: 'voice'
      });

      // Send call invitation message through chat
      if (sendMessageCallback) {
        await sendMessageCallback({
          type: 'call_invitation',
          callType: 'voice',
          callRoomId: callRoomId,
          callerName: callerName,
          text: `${callerName} is inviting you to a voice call`
        });
      }

      toast.success('Arama başlatıldı, karşı taraf aranıyor...');

      // Karşı taraf süresi içinde katılmazsa arama cevapsız işaretlenir
      scheduleMissedCheck(callRoomId);

      // Arayan da odaya katılır; aksi hâlde karşı taraf boş odaya bağlanıyordu
      await joinCallRoom(callRoomId, 'voice');
    } catch (error) {
      console.error('Error initiating voice call:', error);
      toast.error('Arama başlatılamadı.');
    }
  }, [callStatus, joinCallRoom, scheduleMissedCheck]);

  const value = {
    initiateVideoCall,
    initiateVoiceCall,
    joinCallRoom,
    callStatus,
    activeCall
  };

  return (
    <VideoCallContext.Provider value={value}>
      {children}

      {/* Gelen arama - oda daveti (uygulamanın her yerinde görünür) */}
      {incomingRoomInvite && !activeCall && (
        <IncomingCallModal
          show={true}
          callerName={normalizeStoredName(
            incomingRoomInvite.createdByName,
            incomingRoomInvite.createdBy
          )}
          callerEmail={incomingRoomInvite.createdBy}
          callerImage={incomingRoomInvite.callerImage}
          callType={incomingRoomInvite.callType || 'video'}
          onAccept={handleAcceptRoomInvite}
          onReject={handleRejectRoomInvite}
        />
      )}

      {/* Incoming Call Modal (eski doğrudan WebRTC akışı) */}
      {incomingCall && callStatus === 'ringing' && !incomingRoomInvite && (
        <IncomingCallModal
          show={true}
          callerName={incomingCall.callerName}
          callerImage={incomingCall.callerImage}
          callType="video"
          onAccept={handleAcceptCall}
          onReject={handleRejectCall}
        />
      )}

      {/* Jitsi Conference Call */}
      {isJitsiConference && jitsiRoomName && (callStatus === 'connecting' || callStatus === 'connected') && (
        <JitsiMeeting
          roomName={jitsiRoomName}
          displayName={currentUserName}
          email={currentUserEmail}
          isHost={isHost}
          onLeave={handleLeaveCall}
          onEndConference={handleEndConference}
          participants={conferenceParticipants}
        />
      )}

      {/* Regular Video Call (1-on-1) */}
      {activeCall && localStream && !isJitsiConference && (callStatus === 'connecting' || callStatus === 'connected') && (
        <VideoCall
          localStream={localStream}
          remoteStream={remoteStream}
          onEndCall={handleEndCall}
          onLeaveCall={handleLeaveCall}
          onEndConference={handleEndConference}
          isMuted={isMuted}
          isVideoOff={isVideoOff}
          onToggleMute={handleToggleMute}
          onToggleVideo={handleToggleVideo}
          callerName={
            activeCall.receiverId === auth.currentUser?.email
              ? activeCall.callerName
              : activeCall.receiverName
          }
          isInitiator={activeCall.callerId === auth.currentUser?.email}
          isConference={false}
          isHost={isHost}
          participants={conferenceParticipants}
          currentUserEmail={auth.currentUser?.email || ''}
        />
      )}
    </VideoCallContext.Provider>
  );
};
